import React, { useState, useEffect, useRef } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import { useSearchParams } from"react-router-dom";
import Layout from"@/components/Layout";
import { usePagination, TablePagination } from"@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { 
 Users, 
 Plus,
 Search,
 Edit,
 Trash2,
 Phone,
 Building2,
 Calendar,
 DollarSign,
 User,
 ChevronDown,
 ChevronUp,
 AlertCircle,
 Upload,
 FileSpreadsheet,
 CheckCircle2,
 XCircle,
 RefreshCw,
 ArrowRight,
 Loader2
} from"lucide-react";
import { toast } from"sonner";

const EmployeesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [searchParams] = useSearchParams();
 const [loading, setLoading] = useState(true);
 const [employees, setEmployees] = useState([]);
 const [summary, setSummary] = useState(null);
 const [searchTerm, setSearchTerm] = useState("");
 const [filterDepartment, setFilterDepartment] = useState("all");
 const [filterStatus, setFilterStatus] = useState("all");
 const [showDialog, setShowDialog] = useState(false);
 const [editingEmployee, setEditingEmployee] = useState(null);
 const [expandedEmployee, setExpandedEmployee] = useState(null);
 
 // Import state
 const [showImportDialog, setShowImportDialog] = useState(false);
 const [importStep, setImportStep] = useState('upload'); // upload, preview, result
 const [importLoading, setImportLoading] = useState(false);
 const [importPreview, setImportPreview] = useState(null);
 const [importResult, setImportResult] = useState(null);
 const fileInputRef = useRef(null);
 
 const [formData, setFormData] = useState({
 first_name:"",
 last_name:"",
 email:"",
 phone:"",
 role:"",
 department:"",
 employee_id:"",
 start_date: new Date().toISOString().split('T')[0],
 contract_type:"full_time",
 employment_status:"active",
 base_salary:"",
 government_contributions:"",
 extra_salary:"",
 salary_currency:"MKD",
 pay_frequency:"monthly",
 notes:""
 });

 useEffect(() => {
 loadData();
 if (searchParams.get("action") ==="add") {
 setShowDialog(true);
 }
 }, []);

 const loadData = async () => {
 setLoading(true);
 try {
 const [empRes, summaryRes] = await Promise.all([
 axios.get("/employees"),
 axios.get("/employees/summary/overview")
 ]);
 setEmployees(empRes.data);
 setSummary(summaryRes.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleSubmit = async () => {
 if (!formData.first_name || !formData.last_name) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 const payload = {
 ...formData,
 base_salary: formData.base_salary ? parseFloat(formData.base_salary) : null,
 government_contributions: formData.government_contributions ? parseFloat(formData.government_contributions) : null,
 extra_salary: formData.extra_salary ? parseFloat(formData.extra_salary) : null,
 start_date: new Date(formData.start_date).toISOString()
 };

 if (editingEmployee) {
 await axios.patch(`/employees/${editingEmployee.id}`, payload);
 toast.success(t("messages.updateSuccess"));
 } else {
 await axios.post("/employees", payload);
 toast.success(t("messages.saveSuccess"));
 }
 setShowDialog(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const handleDelete = async (id) => {
 if (!window.confirm("Are you sure you want to delete this employee record?")) return;
 
 try {
 await axios.delete(`/employees/${id}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 } catch (error) {
 toast.error(t("messages.deleteFailed"));
 }
 };

 const openEditDialog = (employee) => {
 setEditingEmployee(employee);
 // Support both new combined field and old separate fields
 const govContrib = employee.government_contributions || ((employee.insurance || 0) + (employee.tax || 0));
 setFormData({
 first_name: employee.first_name ||"",
 last_name: employee.last_name ||"",
 email: employee.email ||"",
 phone: employee.phone ||"",
 role: employee.role ||"",
 department: employee.department ||"",
 employee_id: employee.employee_id ||"",
 start_date: employee.start_date ? employee.start_date.split('T')[0] :"",
 contract_type: employee.contract_type ||"full_time",
 employment_status: employee.employment_status ||"active",
 base_salary: employee.base_salary?.toString() ||"",
 government_contributions: govContrib?.toString() ||"",
 extra_salary: employee.extra_salary?.toString() ||"",
 salary_currency: employee.salary_currency ||"MKD",
 pay_frequency: employee.pay_frequency ||"monthly",
 notes: employee.notes ||""
 });
 setShowDialog(true);
 };

 const resetForm = () => {
 setEditingEmployee(null);
 setFormData({
 first_name:"",
 last_name:"",
 email:"",
 phone:"",
 role:"",
 department:"",
 employee_id:"",
 start_date: new Date().toISOString().split('T')[0],
 contract_type:"full_time",
 employment_status:"active",
 base_salary:"",
 government_contributions:"",
 extra_salary:"",
 salary_currency:"MKD",
 pay_frequency:"monthly",
 notes:""
 });
 };

 const formatCurrency = (amount, currency ="MKD") => {
 if (!amount) return"-";
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0
 }).format(amount);
 };

 // Calculate total salary (what employee receives: Base + Extra)
 const getTotalSalary = (emp) => {
 const base = emp.base_salary || 0;
 const extra = emp.extra_salary || 0;
 // Only use old salary field if no new fields exist
 if (base === 0 && extra === 0 && emp.salary) {
 return emp.salary;
 }
 return base + extra;
 };

 // Get government contributions (combined insurance + tax)
 const getGovContrib = (emp) => {
 return emp.government_contributions || ((emp.insurance || 0) + (emp.tax || 0));
 };

 // North Macedonia official salary calculation
 // Calculate Government Contributions from NET (Base) salary
 const calculateGovContributions = (netSalary, currency ="MKD") => {
 if (!netSalary || netSalary <= 0) return { gross: 0, contributions: 0, breakdown: null };
 
 // Only apply MKD formula for MKD currency
 if (currency !=="MKD") {
 return { gross: 0, contributions: 0, breakdown: null };
 }

 // North Macedonia tax rates
 const SOCIAL_RATES = {
 pension: 0.188, // 18.8% - Pension & Disability
 health: 0.075, // 7.5% - Health Insurance
 unemployment: 0.012, // 1.2% - Unemployment Insurance
 injury: 0.005 // 0.5% - Injury / Professional Disease
 };
 const TOTAL_SOCIAL_RATE = 0.28; // 28% total
 const TAX_RATE = 0.10; // 10% income tax
 const PERSONAL_ALLOWANCE = 10270; // Personal tax allowance

 // Formula: NET = GROSS * 0.648 + 1027
 // Therefore: GROSS = (NET - 1027) / 0.648
 const taxAllowanceContribution = PERSONAL_ALLOWANCE * TAX_RATE; // 1,027
 const netFactor = 1 - TOTAL_SOCIAL_RATE - TAX_RATE + (TOTAL_SOCIAL_RATE * TAX_RATE); // 0.648

 const grossSalary = (netSalary - taxAllowanceContribution) / netFactor;
 
 // Calculate individual contributions
 const pension = Math.round(grossSalary * SOCIAL_RATES.pension);
 const health = Math.round(grossSalary * SOCIAL_RATES.health);
 const unemployment = Math.round(grossSalary * SOCIAL_RATES.unemployment);
 const injury = Math.round(grossSalary * SOCIAL_RATES.injury);
 const totalSocial = pension + health + unemployment + injury;
 
 // Calculate tax
 const taxBase = grossSalary - totalSocial - PERSONAL_ALLOWANCE;
 const incomeTax = Math.round(Math.max(0, taxBase) * TAX_RATE);
 
 // Total government contributions
 const totalContributions = totalSocial + incomeTax;

 return {
 gross: Math.round(grossSalary),
 contributions: Math.round(totalContributions),
 breakdown: {
 pension,
 health,
 unemployment,
 injury,
 totalSocial,
 taxBase: Math.round(taxBase),
 incomeTax,
 personalAllowance: PERSONAL_ALLOWANCE
 }
 };
 };

 // Handle base salary change and auto-calculate gov contributions
 const handleBaseSalaryChange = (value) => {
 const baseSalary = parseFloat(value) || 0;
 const calc = calculateGovContributions(baseSalary, formData.salary_currency);
 
 setFormData({
 ...formData, 
 base_salary: value,
 government_contributions: calc.contributions > 0 ? calc.contributions.toString() :""
 });
 };

 // Handle currency change - recalculate if needed
 const handleCurrencyChange = (currency) => {
 const baseSalary = parseFloat(formData.base_salary) || 0;
 const calc = calculateGovContributions(baseSalary, currency);
 
 setFormData({
 ...formData, 
 salary_currency: currency,
 government_contributions: calc.contributions > 0 ? calc.contributions.toString() :""
 });
 };

 // === IMPORT FUNCTIONS ===
 
 // Handle editing a row in import preview
 const handleEditImportRow = (rowIndex, field, value) => {
 if (!importPreview) return;
 
 const updatedRows = importPreview.rows.map(row => {
 if (row.row_index === rowIndex) {
 const updated = { ...row };
 if (field === 'salary') {
 // Parse the new salary value
 const cleanValue = value.replace(/[^\d]/g, '');
 updated.parsed_salary = cleanValue ? parseInt(cleanValue, 10) : null;
 updated.raw_salary = value;
 } else if (field === 'latin_first_name') {
 updated.latin_first_name = value;
 } else if (field === 'latin_last_name') {
 updated.latin_last_name = value;
 } else if (field === 'start_date') {
 updated.start_date = value;
 }
 return updated;
 }
 return row;
 });
 
 setImportPreview({
 ...importPreview,
 rows: updatedRows
 });
 };
 
 const handleImportFileSelect = async (event) => {
 const file = event.target.files[0];
 if (!file) return;
 
 // Validate file type
 const validTypes = ['.csv', '.xlsx', '.xls', '.pdf', '.jpg', '.jpeg', '.png'];
 const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
 if (!validTypes.includes(fileExt)) {
 toast.error(t("validation.required"));
 return;
 }
 
 setImportLoading(true);
 
 try {
 const formData = new FormData();
 formData.append('file', file);
 
 const response = await axios.post('/employees/import/preview', formData, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 
 setImportPreview(response.data);
 setImportStep('preview');
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 } finally {
 setImportLoading(false);
 }
 };
 
 const handleConfirmImport = async () => {
 if (!importPreview) return;
 
 setImportLoading(true);
 
 try {
 const response = await axios.post('/employees/import/confirm', {
 rows: importPreview.rows
 });
 
 setImportResult(response.data);
 setImportStep('result');
 
 // Reload employees
 loadData();
 
 toast.success(`Import complete: ${response.data.created} created, ${response.data.updated} updated`);
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Import failed");
 } finally {
 setImportLoading(false);
 }
 };
 
 const resetImport = () => {
 setShowImportDialog(false);
 setImportStep('upload');
 setImportPreview(null);
 setImportResult(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 };
 
 const getActionBadge = (action) => {
 switch (action) {
 case"CREATE":
 return <Badge className="bg-emerald-500/10 text-emerald-700"><Plus className="w-3 h-3 mr-1"/>Create</Badge>;
 case"UPDATE":
 return <Badge className="bg-blue-500/10 text-blue-700"><RefreshCw className="w-3 h-3 mr-1"/>Update</Badge>;
 case"SKIP":
 return <Badge className="bg-gray-500/10 text-slate-600">Skip</Badge>;
 case"ERROR":
 return <Badge className="bg-rose-500/10 text-rose-700"><XCircle className="w-3 h-3 mr-1"/>Error</Badge>;
 default:
 return <Badge variant="outline">{action}</Badge>;
 }
 };

 // Get total cost (what company pays: Base + Extra + Gov)
 const getTotalCost = (emp) => {
 return getTotalSalary(emp) + getGovContrib(emp);
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return"-";
 return new Date(dateStr).toLocaleDateString();
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case"active":
 return <Badge className="bg-emerald-500/10 text-emerald-700">Active</Badge>;
 case"on_leave":
 return <Badge className="bg-amber-500/10 text-amber-700">On Leave</Badge>;
 case"terminated":
 return <Badge className="bg-rose-500/10 text-rose-700">Terminated</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 const filteredEmployees = employees.filter(emp => {
 const matchesSearch = 
 emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 emp.role?.toLowerCase().includes(searchTerm.toLowerCase());
 const matchesDept = filterDepartment ==="all"|| emp.department === filterDepartment;
 const matchesStatus = filterStatus ==="all"|| emp.employment_status === filterStatus;
 return matchesSearch && matchesDept && matchesStatus;
 });

 const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

 const { currentPage, setCurrentPage, totalPages, paginatedItems: paginatedEmployees, totalItems, startIndex, endIndex } = usePagination(filteredEmployees);

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div className="flex justify-between items-center">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 Employee Management
 </h1>
 <p className="text-[11px] text-slate-500 mt-0.5">
 Manage all employee records and details
 </p>
 </div>
 <div className="flex gap-2">
 <Button 
 variant="outline"
 onClick={() => setShowImportDialog(true)} 
 data-testid="import-payroll-btn"
 className="text-sm"
 >
 <Upload className="w-4 h-4 mr-2"/>
 Import Payroll List
 </Button>
 <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-employee-btn"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200 text-sm">
 <Plus className="w-4 h-4 mr-2"/>
 Add Employee
 </Button>
 </div>
 </div>

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 <Card className=""data-testid="kpi-total-employees">
 <CardContent className="p-4">
 <div className="mb-3">
 <Users className="h-7 w-7 text-indigo-600"/>
 </div>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{summary.total_employees}</p>
 <p className="text-[11px] text-slate-500 mt-0.5">Total Employees</p>
 </CardContent>
 </Card>
 <Card className=""data-testid="kpi-active-employees">
 <CardContent className="p-4">
 <div className="mb-3">
 <User className="h-7 w-7 text-emerald-600"/>
 </div>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{summary.active_employees}</p>
 <p className="text-[11px] text-slate-500 mt-0.5">Active</p>
 </CardContent>
 </Card>
 <Card className=""data-testid="kpi-on-leave">
 <CardContent className="p-4">
 <div className="mb-3">
 <Calendar className="h-7 w-7 text-amber-600"/>
 </div>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{summary.on_leave}</p>
 <p className="text-[11px] text-slate-500 mt-0.5">On Leave</p>
 </CardContent>
 </Card>
 <Card className=""data-testid="kpi-monthly-salaries">
 <CardContent className="p-4">
 <div className="mb-3">
 <DollarSign className="h-7 w-7 text-blue-600"/>
 </div>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{formatCurrency(summary.total_salary_monthly,"EUR")}</p>
 <p className="text-[11px] text-slate-500 mt-0.5">Monthly Salaries (EUR)</p>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Filters */}
 <Card className="">
 <CardContent className="py-4 px-5">
 <div className="flex gap-3">
 <div className="flex-1">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4"/>
 <Input
 placeholder="Search employees..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 h-9"
 data-testid="employee-search-input"
 />
 </div>
 </div>
 <Select value={filterDepartment} onValueChange={setFilterDepartment}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Department"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Departments</SelectItem>
 {departments.map(dept => (
 <SelectItem key={dept} value={dept}>{dept}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 <Select value={filterStatus} onValueChange={setFilterStatus}>
 <SelectTrigger className="w-40">
 <SelectValue placeholder="Status"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Status</SelectItem>
 <SelectItem value="active">Active</SelectItem>
 <SelectItem value="on_leave">On Leave</SelectItem>
 <SelectItem value="terminated">Terminated</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </CardContent>
 </Card>

 {/* Employee List */}
 <Card className="overflow-hidden">
 <div className="px-5 py-4 border-b border-white/40">
 <div className="flex items-center gap-2">
 <Users className="w-4 h-4 text-slate-400"/>
 <span className="text-sm font-medium text-[#334155]">Employees ({filteredEmployees.length})</span>
 </div>
 </div>
 <div>
 {filteredEmployees.length === 0 ? (
 <div className="text-center py-12">
 <Users className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600">No employees found</p>
 <Button className="mt-4"onClick={() => { resetForm(); setShowDialog(true); }}>
 <Plus className="w-4 h-4 mr-2"/>
 Add First Employee
 </Button>
 </div>
 ) : (
 <div className="space-y-0">
 {paginatedEmployees.map((emp) => (
 <div key={emp.id} className="border-b border-white/40 last:border-0"data-testid={`employee-row-${emp.id}`}>
 <div className="px-5 py-3.5 flex items-center justify-between hover:bg-white/80 transition-colors">
 <div className="flex items-center gap-3">
 <div className="w-9 h-9 rounded-full bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
 <span className="text-sm font-medium text-slate-600">
 {emp.first_name?.[0]}{emp.last_name?.[0]}
 </span>
 </div>
 <div>
 <div className="flex items-center gap-2">
 <span className="text-sm font-medium text-slate-900">{emp.first_name} {emp.last_name}</span>
 {getStatusBadge(emp.employment_status)}
 </div>
 {emp.role && <p className="text-[11px] text-slate-500 mt-0.5">{emp.role}</p>}
 </div>
 </div>
 <div className="flex items-center gap-2">
 <div className="text-right mr-3">
 <p className="text-[11px] text-slate-400 font-medium">Total Salary</p>
 <p className="text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(getTotalSalary(emp), emp.salary_currency ||"MKD")}</p>
 </div>
 <Button variant="ghost"size="sm"className="h-8 w-8 p-0"onClick={() => setExpandedEmployee(expandedEmployee === emp.id ? null : emp.id)}>
 {expandedEmployee === emp.id ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
 </Button>
 <Button variant="ghost"size="sm"className="h-8 w-8 p-0"data-testid={`edit-employee-${emp.id}`} onClick={() => openEditDialog(emp)}>
 <Edit className="w-4 h-4"/>
 </Button>
 <Button variant="ghost"size="sm"className="h-8 w-8 p-0 text-slate-400 hover:text-rose-500"data-testid={`delete-employee-${emp.id}`} onClick={() => handleDelete(emp.id)}>
 <Trash2 className="w-4 h-4"/>
 </Button>
 </div>
 </div>
 
 {/* Expanded Details */}
 {expandedEmployee === emp.id && (
 <div className="px-5 py-4 bg-white/50 border-t border-white/40">
 <div className="grid grid-cols-3 gap-6">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Employment Details</p>
 <div className="space-y-1.5 text-sm">
 <p><span className="text-slate-500">Employee ID:</span> <span className="text-[#334155]">{emp.employee_id ||"-"}</span></p>
 <p><span className="text-slate-500">Start Date:</span> <span className="text-[#334155]">{formatDate(emp.start_date)}</span></p>
 <p><span className="text-slate-500">Contract:</span> <span className="text-[#334155]">{emp.contract_type?.replace("_","")}</span></p>
 <p><span className="text-slate-500">Pay Frequency:</span> <span className="text-[#334155]">{emp.pay_frequency}</span></p>
 </div>
 </div>
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Compensation Breakdown</p>
 <div className="space-y-1.5 text-sm">
 <p><span className="text-slate-500">Base Salary:</span> <span className="text-[#334155] tabular-nums">{formatCurrency(emp.base_salary, emp.salary_currency ||"MKD")}</span></p>
 <p><span className="text-slate-500">Gov. Contributions:</span> <span className="text-[#334155] tabular-nums">{formatCurrency(getGovContrib(emp), emp.salary_currency ||"MKD")}</span></p>
 <p><span className="text-slate-500">Extra Salary:</span> <span className="text-[#334155] tabular-nums">{formatCurrency(emp.extra_salary, emp.salary_currency ||"MKD")}</span></p>
 <p className="font-medium border-t border-slate-200 pt-1.5 mt-1.5">
 <span className="text-slate-600">Total:</span> <span className="text-slate-900 tabular-nums">{formatCurrency(getTotalSalary(emp), emp.salary_currency ||"MKD")}</span>
 </p>
 </div>
 </div>
 {emp.notes && (
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Notes</p>
 <p className="text-sm text-slate-500">{emp.notes}</p>
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={setCurrentPage} />
 </Card>
 </div>

 {/* Add/Edit Employee Dialog */}
 <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
 <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editingEmployee ?"Edit Employee":"Add New Employee"}</DialogTitle>
 </DialogHeader>
 <div className="space-y-6 py-4">
 {/* Basic Info */}
 <div>
 <h3 className="font-semibold text-slate-900 mb-3">Basic Information</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>First Name *</Label>
 <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} />
 </div>
 <div>
 <Label>Last Name *</Label>
 <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} />
 </div>
 <div>
 <Label>Phone</Label>
 <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
 </div>
 <div>
 <Label>Position</Label>
 <Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="e.g., Software Engineer"/>
 </div>
 </div>
 </div>

 {/* Employment Details */}
 <div>
 <h3 className="font-semibold text-slate-900 mb-3">Employment Details</h3>
 <div className="grid grid-cols-4 gap-4">
 <div>
 <Label>Employee ID</Label>
 <Input value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} />
 </div>
 <div>
 <Label>Start Date</Label>
 <Input type="date"value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} />
 </div>
 <div>
 <Label>Contract Type</Label>
 <Select value={formData.contract_type} onValueChange={(v) => setFormData({...formData, contract_type: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="full_time">Full Time</SelectItem>
 <SelectItem value="part_time">Part Time</SelectItem>
 <SelectItem value="contractor">Contractor</SelectItem>
 <SelectItem value="intern">Intern</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Status</Label>
 <Select value={formData.employment_status} onValueChange={(v) => setFormData({...formData, employment_status: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="active">Active</SelectItem>
 <SelectItem value="on_leave">On Leave</SelectItem>
 <SelectItem value="terminated">Terminated</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 </div>

 {/* Compensation */}
 <div>
 <h3 className="font-semibold text-slate-900 mb-3">Compensation</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Base Salary (NET)</Label>
 <Input 
 type="number"
 value={formData.base_salary} 
 onChange={(e) => handleBaseSalaryChange(e.target.value)} 
 placeholder="Net salary employee receives"
 />
 </div>
 <div>
 <Label>Gov. Contributions (Insurance + Tax)</Label>
 <div className="relative">
 <Input 
 type="number"
 value={formData.government_contributions} 
 readOnly={formData.salary_currency ==="MKD"}
 onChange={(e) => formData.salary_currency !=="MKD"&& setFormData({...formData, government_contributions: e.target.value})}
 className={formData.salary_currency ==="MKD"?"bg-white/60 cursor-not-allowed":""}
 placeholder={formData.salary_currency ==="MKD"?"Auto-calculated":"Insurance + Tax to government"} 
 />
 {formData.salary_currency ==="MKD"&& formData.base_salary && (
 <div className="mt-1 text-[11px] text-slate-500">
 Auto-calculated using North Macedonia tax formula
 </div>
 )}
 </div>
 </div>
 <div>
 <Label>Extra Salary (Bonus)</Label>
 <Input type="number"value={formData.extra_salary} onChange={(e) => setFormData({...formData, extra_salary: e.target.value})} placeholder="Additional bonus"/>
 </div>
 <div>
 <Label>Currency</Label>
 <Select value={formData.salary_currency} onValueChange={handleCurrencyChange}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="MKD">MKD (Auto-calc tax)</SelectItem>
 <SelectItem value="EUR">EUR</SelectItem>
 <SelectItem value="USD">USD</SelectItem>
 <SelectItem value="ALL">ALL</SelectItem>
 </SelectContent>
 </Select>
 </div>
 {/* Tax Breakdown - Only show for MKD when base salary is entered */}
 {formData.salary_currency ==="MKD"&& formData.base_salary && parseFloat(formData.base_salary) > 0 && (
 <div className="col-span-2 p-4 bg-blue-50/80 rounded-sm border border-blue-200/60">
 <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
 <AlertCircle className="w-4 h-4"/>
 Tax Calculation Breakdown (North Macedonia)
 </h4>
 {(() => {
 const calc = calculateGovContributions(parseFloat(formData.base_salary),"MKD");
 return (
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <p className="text-slate-600">Calculated GROSS Salary:</p>
 <p className="font-semibold">{calc.gross?.toLocaleString()} MKD</p>
 </div>
 <div>
 <p className="text-slate-600">Social Contributions (28%):</p>
 <ul className="text-[11px] text-slate-500 ml-2">
 <li>Pension (18.8%): {calc.breakdown?.pension?.toLocaleString()} MKD</li>
 <li>Health (7.5%): {calc.breakdown?.health?.toLocaleString()} MKD</li>
 <li>Unemployment (1.2%): {calc.breakdown?.unemployment?.toLocaleString()} MKD</li>
 <li>Injury (0.5%): {calc.breakdown?.injury?.toLocaleString()} MKD</li>
 </ul>
 <p className="font-semibold mt-1">Total: {calc.breakdown?.totalSocial?.toLocaleString()} MKD</p>
 </div>
 <div>
 <p className="text-slate-600">Personal Allowance:</p>
 <p className="font-semibold">{calc.breakdown?.personalAllowance?.toLocaleString()} MKD</p>
 </div>
 <div>
 <p className="text-slate-600">Tax Base:</p>
 <p className="font-semibold">{calc.breakdown?.taxBase?.toLocaleString()} MKD</p>
 </div>
 <div>
 <p className="text-slate-600">Income Tax (10%):</p>
 <p className="font-semibold">{calc.breakdown?.incomeTax?.toLocaleString()} MKD</p>
 </div>
 <div className="bg-blue-100 p-2 rounded">
 <p className="text-blue-800 font-semibold">Gov. Contributions Total:</p>
 <p className="text-lg font-bold text-blue-900">{calc.contributions?.toLocaleString()} MKD</p>
 </div>
 </div>
 );
 })()}
 </div>
 )}
 <div className={formData.salary_currency ==="MKD"&& formData.base_salary ?"":"col-span-2"}>
 <Label>Pay Frequency</Label>
 <Select value={formData.pay_frequency} onValueChange={(v) => setFormData({...formData, pay_frequency: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="monthly">Monthly</SelectItem>
 <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
 <SelectItem value="weekly">Weekly</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 </div>

 {/* Notes */}
 <div>
 <Label>Notes</Label>
 <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline"onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
 <Button onClick={handleSubmit}>{editingEmployee ?"Save Changes":"Add Employee"}</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Import Payroll Dialog */}
 <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) resetImport(); }}>
 <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <FileSpreadsheet className="w-5 h-5"/>
 Import Payroll List
 </DialogTitle>
 </DialogHeader>
 
 {/* Step 1: Upload */}
 {importStep === 'upload' && (
 <div className="py-8">
 <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center hover:border-blue-400 transition-colors">
 <input
 ref={fileInputRef}
 type="file"
 accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png"
 onChange={handleImportFileSelect}
 className="hidden"
 id="import-file-input"
 />
 {importLoading ? (
 <div className="flex flex-col items-center">
 <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4"/>
 <p className="text-slate-600">Processing file...</p>
 <p className="text-sm text-slate-500">Extracting data and transliterating names</p>
 </div>
 ) : (
 <>
 <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4"/>
 <p className="text-lg font-medium text-slate-900 mb-2">
 Upload Payroll Document
 </p>
 <p className="text-slate-500 mb-4">
 Supports: CSV, Excel (.xlsx, .xls), PDF, Images (JPG, PNG)
 </p>
 <Button onClick={() => fileInputRef.current?.click()}>
 <Upload className="w-4 h-4 mr-2"/>
 Select File
 </Button>
 <div className="mt-6 text-left bg-blue-50/80 p-4 rounded-sm">
 <h4 className="font-semibold text-blue-900 mb-2">Document Requirements:</h4>
 <ul className="text-sm text-blue-800 space-y-1">
 <li>• Document can be in Macedonian Cyrillic (names will be transliterated)</li>
 <li>• Should contain columns: Name (Име/Презиме), Salary (Нето/Износ)</li>
 <li>• Personal ID (Мат.Бр/EMBG) is optional but helps match existing employees</li>
 <li>• Salary values will be parsed as NET salary in MKD</li>
 </ul>
 </div>
 </>
 )}
 </div>
 </div>
 )}
 
 {/* Step 2: Preview */}
 {importStep === 'preview' && importPreview && (
 <div className="py-4">
 {/* Summary Cards */}
 <div className="grid grid-cols-4 gap-4 mb-6">
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-slate-500">To Create</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.create_count}</p>
 </div>
 <Plus className="h-6 w-6 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-slate-500">To Update</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.update_count}</p>
 </div>
 <RefreshCw className="h-6 w-6 text-blue-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-slate-500">To Skip</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.skip_count}</p>
 </div>
 <ArrowRight className="w-6 h-6 text-gray-400"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-slate-500">Errors</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.error_count}</p>
 </div>
 <XCircle className="h-6 w-6 text-rose-600"/>
 </div>
 </CardContent>
 </Card>
 </div>
 
 {/* Preview Table */}
 <div className="mb-3 p-3 bg-yellow-50/80 border border-yellow-200/60 rounded-sm">
 <p className="text-sm text-yellow-800">
 <AlertCircle className="w-4 h-4 inline mr-1"/>
 <strong>Review before importing:</strong> AI-extracted values may have errors. Click on any name or salary to edit it.
 </p>
 </div>
 <div className="border rounded-sm overflow-hidden max-h-96 overflow-y-auto">
 <table className="w-full text-sm">
 <thead className="bg-white/60 sticky top-0">
 <tr>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">#</th>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">Cyrillic Name</th>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">First Name (editable)</th>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">Last Name (editable)</th>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">Mat.Br</th>
 <th className="px-3 py-2 text-right font-medium text-[#334155]">NET Salary</th>
 <th className="px-3 py-2 text-center font-medium text-[#334155]">Start Date</th>
 <th className="px-3 py-2 text-center font-medium text-[#334155]">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-200">
 {importPreview.rows.map((row) => (
 <tr key={row.row_index} className={row.action === 'ERROR' ? 'bg-red-50/80' : ''}>
 <td className="px-3 py-2 text-sm text-slate-500">{row.row_index}</td>
 <td className="px-3 py-2">
 <span className="text-slate-500 text-xs">
 {row.cyrillic_last_name} {row.cyrillic_first_name}
 </span>
 </td>
 <td className="px-3 py-2">
 <input
 type="text"
 className="w-28 px-2 py-1 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={row.latin_first_name || ''}
 onChange={(e) => handleEditImportRow(row.row_index, 'latin_first_name', e.target.value)}
 placeholder="First name"
 />
 </td>
 <td className="px-3 py-2">
 <input
 type="text"
 className="w-28 px-2 py-1 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={row.latin_last_name || ''}
 onChange={(e) => handleEditImportRow(row.row_index, 'latin_last_name', e.target.value)}
 placeholder="Last name"
 />
 </td>
 <td className="px-3 py-2 text-slate-600 font-mono text-xs">{row.mat_br || '-'}</td>
 <td className="px-3 py-2 text-right">
 <input
 type="text"
 className="w-24 px-2 py-1 text-right font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={row.parsed_salary ? row.parsed_salary.toLocaleString() : ''}
 onChange={(e) => handleEditImportRow(row.row_index, 'salary', e.target.value)}
 placeholder="Salary"
 />
 <span className="ml-1 text-slate-500">MKD</span>
 </td>
 <td className="px-3 py-2 text-center">
 <input
 type="date"
 className="w-32 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={row.start_date || ''}
 onChange={(e) => handleEditImportRow(row.row_index, 'start_date', e.target.value)}
 />
 </td>
 <td className="px-3 py-2 text-center">
 <div className="flex flex-col items-center gap-1">
 {getActionBadge(row.action)}
 {row.error_message && (
 <span className="text-xs text-red-600">{row.error_message}</span>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 
 <DialogFooter className="mt-6">
 <Button variant="outline"onClick={resetImport}>Cancel</Button>
 <Button variant="outline"onClick={() => { setImportStep('upload'); setImportPreview(null); }}>
 Upload Different File
 </Button>
 <Button 
 onClick={handleConfirmImport} 
 disabled={importLoading || importPreview.valid_rows === 0}
 >
 {importLoading ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
 Importing...
 </>
 ) : (
 <>
 <CheckCircle2 className="w-4 h-4 mr-2"/>
 Confirm Import ({importPreview.valid_rows} records)
 </>
 )}
 </Button>
 </DialogFooter>
 </div>
 )}
 
 {/* Step 3: Result */}
 {importStep === 'result' && importResult && (
 <div className="py-8 text-center">
 <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4"/>
 <h3 className="text-xl font-semibold text-slate-900 mb-6">Import Complete!</h3>
 
 <div className="grid grid-cols-4 gap-4 mb-6 max-w-2xl mx-auto">
 <div className="bg-green-50/80 rounded-sm p-4">
 <p className="text-2xl font-semibold tabular-nums text-green-700">{importResult.created}</p>
 <p className="text-sm text-green-600">Created</p>
 </div>
 <div className="bg-blue-50/80 rounded-sm p-4">
 <p className="text-2xl font-semibold tabular-nums text-blue-700">{importResult.updated}</p>
 <p className="text-sm text-blue-600">Updated</p>
 </div>
 <div className="bg-gray-50 rounded-sm p-4">
 <p className="text-2xl font-semibold tabular-nums text-slate-600">{importResult.skipped}</p>
 <p className="text-sm text-slate-500">Skipped</p>
 </div>
 <div className="bg-red-50/80 rounded-sm p-4">
 <p className="text-2xl font-semibold tabular-nums text-red-700">{importResult.errors}</p>
 <p className="text-sm text-red-600">Errors</p>
 </div>
 </div>
 
 {importResult.error_details && importResult.error_details.length > 0 && (
 <div className="bg-red-50/80 rounded-sm p-4 text-left max-w-2xl mx-auto mb-6">
 <h4 className="font-semibold text-red-800 mb-2">Error Details:</h4>
 <ul className="text-sm text-red-700 space-y-1">
 {importResult.error_details.map((err, idx) => (
 <li key={idx}>• {err}</li>
 ))}
 </ul>
 </div>
 )}
 
 <Button onClick={resetImport}>
 Done
 </Button>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default EmployeesPage;
